<template>
  <div class="space-y-5" data-test="change-summary">
    <p v-if="!changes.document.length && !changes.prompt.length" class="text-gray-600" data-test="no-readable-changes">
      {{ technical.length ? 'Only technical settings changed. See Technical details.' : 'No differences.' }}
    </p>

    <section v-if="changes.document.length" data-test="document-changes">
      <h3 :class="HEADING">Document</h3>
      <ul class="space-y-2">
        <li v-for="c in changes.document" :key="`${c.kind}-${c.key}`" class="flex gap-3" :data-test="`doc-change-${c.kind}`">
          <span :class="['mt-0.5 w-5 flex-none text-center font-semibold', KIND[c.kind].tone]" :title="KIND[c.kind].label" aria-hidden="true">{{ KIND[c.kind].mark }}</span>
          <div class="min-w-0 flex-1">
            <div class="text-gray-900"><span class="font-medium">{{ c.title }}</span><span class="text-gray-600">: {{ c.detail ? `${KIND[c.kind].label}, ${c.detail}` : KIND[c.kind].label }}</span></div>
            <ul v-if="c.changes && c.changes.length" class="mt-1 space-y-1">
              <li v-for="(d, k) in c.changes" :key="k" class="text-gray-700">
                {{ d.text }}
                <HermesSideBySide v-if="d.diff" class="mt-1" :parts="d.diff" :before-label="beforeLabel" :after-label="afterLabel" />
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </section>

    <section v-if="changes.prompt.length" data-test="prompt-changes">
      <h3 :class="HEADING">General instructions</h3>
      <ul class="space-y-3">
        <li v-for="(c, k) in changes.prompt" :key="`${k}-${c.kind}`" class="flex gap-3" :data-test="`prompt-change-${c.kind}`">
          <span :class="['mt-0.5 w-5 flex-none text-center font-semibold', KIND[c.kind].tone]" :title="KIND[c.kind].label" aria-hidden="true">{{ KIND[c.kind].mark }}</span>
          <div class="min-w-0 flex-1">
            <div class="text-gray-600">{{ KIND[c.kind].label }}<span v-if="c.under"> under “{{ c.under }}”</span><span v-if="c.words"> · {{ c.words }} words</span></div>
            <HermesSideBySide class="mt-1" :parts="c.diff" :before-label="beforeLabel" :after-label="afterLabel" />
          </div>
        </li>
      </ul>
    </section>

    <div class="flex gap-4 border-t border-gray-200 pt-3">
      <button type="button" :class="TEXT_BUTTON" data-test="toggle-preview" @click="panel = panel === 'preview' ? '' : 'preview'">{{ panel === 'preview' ? 'Hide' : `Preview the ${reportLabel ? reportLabel.toLowerCase() : 'document'}` }}</button>
      <button type="button" :class="TEXT_BUTTON" data-test="toggle-technical" @click="panel = panel === 'technical' ? '' : 'technical'">{{ panel === 'technical' ? 'Hide technical details' : 'Technical details' }}</button>
    </div>

    <div v-if="panel === 'preview' && reportLabel" class="grid grid-cols-2 gap-4" data-test="report-preview">
      <div v-for="pane in previews" :key="pane.label">
        <div class="mb-2 text-[11px] font-medium uppercase tracking-wider text-gray-500">{{ pane.label }}</div>
        <div class="max-h-[50vh] overflow-y-auto rounded border border-gray-200 bg-gray-50 p-3">
          <ReportLayout v-if="pane.report" :display-schema="pane.report" outline />
        </div>
      </div>
      <p class="col-span-2 text-gray-500">The sections and what each one shows. Try the draft to see real output.</p>
    </div>

    <div v-if="panel === 'preview' && !reportLabel" class="grid grid-cols-2 gap-4" data-test="document-preview">
      <div v-for="pane in previews" :key="pane.label">
        <div class="mb-2 text-[11px] font-medium uppercase tracking-wider text-gray-500">{{ pane.label }}</div>
        <div class="max-h-[50vh] overflow-y-auto rounded border border-gray-200 bg-gray-50 p-3">
          <SummaryDocumentPage :title="title" :sections="pane.sections" :content="pane.content" />
        </div>
      </div>
      <p class="col-span-2 text-gray-500">Placeholder text shows where each section goes. Run the draft on an engagement to see real output.</p>
    </div>

    <div v-if="panel === 'technical'" class="space-y-4" data-test="technical-details">
      <p v-if="!technical.length" class="text-gray-600">No differences.</p>
      <div v-for="d in technical" :key="d.key">
        <h3 :class="HEADING">{{ DIFF_LABELS[d.key] || d.key }}</h3>
        <pre class="whitespace-pre-wrap break-words"><span v-for="(l, k) in d.lines" :key="k" :class="['block px-2', l.op === '-' ? 'bg-gray-100 line-through text-gray-600' : (l.op === '+' ? 'bg-secondary-100 text-gray-900' : 'text-gray-700')]">{{ l.op }} {{ l.text }}</span></pre>
      </div>
    </div>
  </div>
</template>

<script>
import ReportLayout from '@/components/PersonalAnalyst/ReportLayout.vue';
import { TEAM_REVIEW_KIND } from '@/services/api/hermesTemplates';
import SummaryDocumentPage from '@/components/Care_Management/Widgets/PatientEngagement/SummaryDocumentPage.vue';
import { sampleContent, sectionsFrom } from './documentSections';
import HermesSideBySide from './HermesSideBySide.vue';
import { TEXT_BUTTON, diffParts, reportSchema } from './hermes';
import { templateChanges } from './templateChanges';

const DIFF_KEYS = ['prompt', 'result_schema', 'display'];
const DIFF_LABELS = { prompt: 'Prompt', result_schema: 'Result schema', display: 'Display' };
const HEADING = 'text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2';
const KIND = {
  added: { mark: '+', label: 'added', tone: 'text-secondary-700' },
  removed: { mark: '−', label: 'removed', tone: 'text-gray-500' },
  moved: { mark: '↕', label: 'moved', tone: 'text-gray-700' },
  changed: { mark: '✎', label: 'changed', tone: 'text-gray-700' },
};

export default {
  name: 'HermesChangeSummary',
  components: { HermesSideBySide, ReportLayout, SummaryDocumentPage },
  props: {
    // Two versions of a document template: { prompt, result_schema, display }.
    before: { type: Object, required: true },
    after: { type: Object, required: true },
    title: { type: String, default: 'Document' },
    beforeLabel: { type: String, default: 'Before' },
    afterLabel: { type: String, default: 'After' },
    // The template's kind, for the preview's label.
    kind: { type: String, default: '' },
  },
  data() {
    return { panel: '', HEADING, KIND, TEXT_BUTTON, DIFF_LABELS };
  },
  computed: {
    // A report-style template (display_schema, e.g. a team review) is previewed with a real result, since its layout
    // isn't built from its sections.
    reportLabel() {
      if (!(reportSchema(this.after.display) || reportSchema(this.before.display))) return '';
      return this.kind === TEAM_REVIEW_KIND ? 'Patient summary' : 'Report';
    },
    changes() {
      return templateChanges(this.side(this.before), this.side(this.after));
    },
    technical() {
      return diffParts(this.side(this.before), this.side(this.after), DIFF_KEYS);
    },
    previews() {
      return [[this.beforeLabel, this.before], [this.afterLabel, this.after]].map(([label, t]) => {
        const display = (t && t.display) || {};
        return { label, report: reportSchema(display), sections: display.display_sections || [], content: sampleContent(sectionsFrom((t || {}).result_schema, display)) };
      });
    },
  },
  methods: {
    side(t) {
      return { prompt: (t && t.prompt) || '', result_schema: (t && t.result_schema) || null, display: (t && t.display) || {} };
    },
  },
};
</script>
